import { useParams } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { usePeer } from '../hooks/usePeer'
import { useAuth } from '../contexts/AuthContext'
import StatusBar, { getStatusText } from '../components/StatusBar'
import CallRating from '../components/CallRating'
import CallControls from '../components/CallControls'
import LocalVideo from '../components/LocalVideo'
import ShareLink from '../components/ShareLink'
import Avatar from '../components/Avatar'

function Room() {
  const { roomId } = useParams()
  const { user } = useAuth()
  const [localStream, setLocalStream] = useState(null)
  const [isVideoOff, setIsVideoOff] = useState(false)
  const [isScreenSharing, setIsScreenSharing] = useState(false)
  const [facingMode, setFacingMode] = useState('user')
  const remoteVideoRef = useRef(null)
  const videosRef = useRef(null)

  const { remoteStream, connectionStatus, endCall, remoteUser, callQuality, callRef } = usePeer(roomId, localStream, user)

  // Get local media stream
  useEffect(() => {
    async function getMedia() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        })
        setLocalStream(stream)
      } catch (err) {
        console.error('Failed to get media:', err)
        Sentry.captureException(err)
        alert('Could not access camera/microphone. Please allow permissions.')
      }
    }
    getMedia()

    return () => {
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop())
      }
    }
  }, [])

  // Set remote video
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream
    }
  }, [remoteStream])

  const switchCamera = async () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user'
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newMode },
        audio: false
      })
      const newTrack = newStream.getVideoTracks()[0]

      // Replace track in peer connection so remote sees the new camera
      const pc = callRef.current?.peerConnection
      if (pc) {
        const sender = pc.getSenders().find(s => s.track?.kind === 'video')
        if (sender) await sender.replaceTrack(newTrack)
      }

      // Replace track in local stream
      const oldTrack = localStream.getVideoTracks()[0]
      localStream.removeTrack(oldTrack)
      localStream.addTrack(newTrack)
      oldTrack.stop()

      setFacingMode(newMode)
    } catch (err) {
      console.error('Failed to switch camera:', err)
      Sentry.captureException(err)
    }
  }

  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      // Revert to camera
      try {
        const camStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: false
        })
        const camTrack = camStream.getVideoTracks()[0]

        const pc = callRef.current?.peerConnection
        if (pc) {
          const sender = pc.getSenders().find(s => s.track?.kind === 'video')
          if (sender) await sender.replaceTrack(camTrack)
        }

        const oldTrack = localStream.getVideoTracks()[0]
        localStream.removeTrack(oldTrack)
        localStream.addTrack(camTrack)
        oldTrack.stop()

        setIsScreenSharing(false)
      } catch (err) {
        console.error('Failed to revert to camera:', err)
        Sentry.captureException(err)
      }
    } else {
      // Start screen share
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true })
        const screenTrack = screenStream.getVideoTracks()[0]

        const pc = callRef.current?.peerConnection
        if (pc) {
          const sender = pc.getSenders().find(s => s.track?.kind === 'video')
          if (sender) await sender.replaceTrack(screenTrack)
        }

        const oldTrack = localStream.getVideoTracks()[0]
        localStream.removeTrack(oldTrack)
        localStream.addTrack(screenTrack)
        oldTrack.stop()

        setIsScreenSharing(true)

        screenTrack.onended = () => toggleScreenShare()
      } catch (err) {
        // User cancelled the picker — do nothing
        if (err.name !== 'NotAllowedError') {
          console.error('Failed to share screen:', err)
          Sentry.captureException(err)
        }
      }
    }
  }

  const handleEndCall = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop())
    }
    endCall()
  }

  return (
    <div className="room">
      <StatusBar connectionStatus={connectionStatus} callQuality={callQuality} />

      <div className="videos" ref={videosRef}>
        {remoteUser && (
          <span className="remote-user-info">
            <Avatar photoURL={remoteUser.photoURL} displayName={remoteUser.displayName} />
          </span>
        )}
        <div className="video-container remote">
          {remoteStream ? (
            <video ref={remoteVideoRef} autoPlay playsInline />
          ) : (
            <div className="video-placeholder">
              {connectionStatus === 'waiting' ? (
                <ShareLink />
              ) : connectionStatus === 'ended' ? (
                <CallRating roomId={roomId} />
              ) : connectionStatus === 'error' ? (
                <p className="status-message">{getStatusText(connectionStatus)}</p>
              ) : (
                <div className="loader" />
              )}
            </div>
          )}
        </div>
        {connectionStatus !== 'ended' && <LocalVideo localStream={localStream} isVideoOff={isVideoOff} containerRef={videosRef} />}
      </div>
      {connectionStatus !== 'ended' &&
        <CallControls
          localStream={localStream}
          isVideoOff={isVideoOff}
          isScreenSharing={isScreenSharing}
          onToggleVideo={setIsVideoOff}
          onSwitchCamera={switchCamera}
          onToggleScreenShare={toggleScreenShare}
          onEndCall={handleEndCall}
        />}
    </div>
  )
}

export default Room
